import axios from 'axios';
import errorHandler from './errorHandler.js';

export default function (baseUrl, messageService) {
  const handleError = errorHandler(messageService);
  const reportError = error => {
    return error.response?.status !== 409;
  };
  const url = baseUrl || 'http://localhost:8881';
  const conf = token => {
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  const modifyTemplate = async (
    customer,
    libraryId,
    templateId,
    body,
    conf
  ) => {
    await axios.patch(
      `${url}/customers/${customer}/libraries/${libraryId}/templates/${templateId}`,
      body,
      conf
    );
  };

  const buildTemplate = (
    templateName,
    jsonTemplate,
    htmlTemplate,
    mergeTagsSource = undefined,
    tags = undefined
  ) => {
    const template = {
      name: templateName,
      template: jsonTemplate,
      htmlTemplate
    };

    if (mergeTagsSource !== undefined) {
      template.mergeTagsSource = mergeTagsSource;
    }

    if (tags !== undefined) {
      template.tags = tags;
    }

    return template;
  };

  return {
    getTemplates: async (token, customer, library, page, filters = {}) => {
      try {
        let pageOnServer = 0;
        if (page !== undefined && Number.isInteger(page) && page > 0) {
          pageOnServer = page - 1;
        }
        const path = new URL(
          `${url}/customers/${customer}/libraries/${library}/templates`
        );
        path.searchParams.append('page', pageOnServer);
        path.searchParams.append('size', 8);
        const {name, selectedTags} = filters;
        if (name !== undefined && name !== '') {
          path.searchParams.append('name', name);
        }
        if (selectedTags !== undefined && selectedTags.length > 0) {
          path.searchParams.append('tags', selectedTags);
        }
        const response = await axios.get(path.toString(), conf(token));
        return response.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    getTemplate: async (
      token,
      customer,
      library,
      templateId,
      messageModelId
    ) => {
      let completeUrl = `${url}/customers/${customer}/libraries/${library}/templates/${templateId}`;
      if (messageModelId !== undefined) {
        completeUrl = completeUrl + `?messageModelId=${messageModelId}`;
      }
      try {
        const response = await axios.get(completeUrl, conf(token));
        return response.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    getLibraries: async (token, customer) => {
      try {
        const response = await axios.get(
          `${url}/customers/${customer}/libraries`,
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    saveTemplate: async (
      token,
      customer,
      libraryId,
      templateName,
      jsonTemplate,
      htmlTemplate,
      mergeTagsSource = undefined
    ) => {
      const template = buildTemplate(
        templateName,
        jsonTemplate,
        htmlTemplate,
        mergeTagsSource
      );

      try {
        const response = await axios.post(
          `${url}/customers/${customer}/libraries/${libraryId}/templates`,
          template,
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error, false); // don't report conflict it's expected
      }
    },
    updateTemplate: async (
      token,
      customer,
      libraryId,
      templateId,
      templateName,
      jsonTemplate,
      htmlTemplate,
      mergeTagsSource = undefined,
      tags = undefined
    ) => {
      const template = buildTemplate(
        templateName,
        jsonTemplate,
        htmlTemplate,
        mergeTagsSource,
        tags
      );
      template.id = templateId;

      try {
        const response = await axios.put(
          `${url}/customers/${customer}/libraries/${libraryId}/templates/${templateId}`,
          template,
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    deleteTemplate: async (token, customer, libraryId, templateId) => {
      try {
        await axios.delete(
          `${url}/customers/${customer}/libraries/${libraryId}/templates/${templateId}`,
          conf(token)
        );
      } catch (error) {
        throw handleError(error);
      }
    },
    renameTemplate: async (
      token,
      customer,
      libraryId,
      templateId,
      templateName
    ) => {
      try {
        await modifyTemplate(
          customer,
          libraryId,
          templateId,
          {name: templateName},
          conf(token)
        );
      } catch (error) {
        throw handleError(error);
      }
    },
    shareTemplate: async (token, customer, libraryId, templateId, shared) => {
      try {
        await modifyTemplate(
          customer,
          libraryId,
          templateId,
          {shared},
          conf(token)
        );
      } catch (error) {
        throw handleError(error);
      }
    },
    downloadHtmlTemplate: async (token, customer, libraryId, templateId) => {
      try {
        const completeUrl = `${url}/customers/${customer}/libraries/${libraryId}/templates/${templateId}/html`;
        const response = await axios.get(completeUrl, conf(token));
        return response.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    assignMergeTagsSourceToTemplate: async (
      token,
      customer,
      libraryId,
      templateId,
      mergeTagsSource
    ) => {
      try {
        await modifyTemplate(
          customer,
          libraryId,
          templateId,
          {mergeTagsSource},
          conf(token)
        );
      } catch (error) {
        throw handleError(error);
      }
    },
    mergeTagsSources: async (token, customer, {page = 0, size = 8} = {}) => {
      try {
        const response = await axios.get(
          `${baseUrl}/customers/${customer}/merge-tags-source-types/SEND/sources?page=${page}&size=${size}`,
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    getDisplayConditions: async (
      token,
      customer,
      sourceId,
      {page = 0, size = 10} = {}
    ) => {
      try {
        const response = await axios.get(
          `${baseUrl}/customers/${customer}/merge-tags-source-types/SEND/sources/${sourceId}/display-conditions?page=${page}&size=${size}&sort=id,DESC`,
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    getDisplayCondition: async (token, customer, sourceId, conditionId) => {
      try {
        const response = await axios.get(
          `${baseUrl}/customers/${customer}/merge-tags-source-types/SEND/sources/${sourceId}/display-conditions/${conditionId}`,
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    getSourceFields: async (
      token,
      customer,
      sourceId,
      {page = 0, size = 50} = {}
    ) => {
      try {
        const response = await axios.get(
          `${baseUrl}/customers/${customer}/merge-tags-source-types/SEND/sources/${sourceId}/fields?page=${page}&size=${size}`,
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    getSourceFieldValues: async (
      token,
      customer,
      sourceId,
      field,
      {page = 0, size = 1500} = {}
    ) => {
      try {
        const response = await axios.get(
          `${baseUrl}/customers/${customer}/merge-tags-source-types/SEND/sources/${sourceId}/fields/${field}/values?page=${page}&size=${size}`,
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    parseDisplayCondition: async (token, customer, sourceId, condition) => {
      try {
        const response = await axios.post(
          `${baseUrl}/customers/${customer}/merge-tags-source-types/SEND/sources/${sourceId}/display-conditions?dry-run=true`,
          condition,
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    saveDisplayCondition: async (token, customer, sourceId, condition) => {
      try {
        const response = await axios.post(
          `${baseUrl}/customers/${customer}/merge-tags-source-types/SEND/sources/${sourceId}/display-conditions`,
          condition,
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    updateDisplayCondition: async (
      token,
      customer,
      sourceId,
      conditionId,
      condition
    ) => {
      try {
        const response = await axios.put(
          `${baseUrl}/customers/${customer}/merge-tags-source-types/SEND/sources/${sourceId}/display-conditions/${conditionId}`,
          condition,
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    deleteDisplayCondition: async (token, customer, sourceId, conditionId) => {
      try {
        const response = await axios.delete(
          `${baseUrl}/customers/${customer}/merge-tags-source-types/SEND/sources/${sourceId}/display-conditions/${conditionId}`,
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    getTags: async (token, customer) => {
      try {
        const response = await axios.get(
          `${url}/customers/${customer}/tags`,
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    saveTags: async (token, customer, tags) => {
      try {
        const response = await axios.put(
          `${url}/customers/${customer}/tags`,
          {
            tags
          },
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    saveDynamicContent: async (token, customer, content) => {
      try {
        const response = await axios.put(
          `${url}/customers/${customer}/merge-contents`,
          content,
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    customerInfo: async (token, customer) => {
      try {
        const response = await axios.get(
          `${url}/customers/${customer}`,
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    getRowCategories: async (token, customer, {page = 0, size = 20} = {}) => {
      try {
        const response = await axios.get(
          `${url}/customers/${customer}/rows/categories?page=${page}&size=${size}`,
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error, reportError(error));
      }
    },
    saveRowCategory: async (token, customer, {name}) => {
      try {
        const response = await axios.post(
          `${url}/customers/${customer}/rows/categories`,
          {name},
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error, reportError(error));
      }
    },
    updateRowCategory: async (token, customer, id, {name}) => {
      try {
        const response = await axios.patch(
          `${url}/customers/${customer}/rows/categories/${id}`,
          {name},
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error, reportError(error));
      }
    },
    deleteRowCategory: async (token, customer, id) => {
      try {
        const response = await axios.delete(
          `${url}/customers/${customer}/rows/categories/${id}`,
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error, reportError(error));
      }
    },
    getRows: async (
      token,
      customer,
      categoryId,
      {page = 0, size = 30} = {}
    ) => {
      try {
        const requestUrl = `${url}/customers/${customer}/rows?categoryId=${categoryId}&page=${page}&size=${size}`;
        const response = await axios.get(requestUrl, conf(token));
        return response.data;
      } catch (error) {
        throw handleError(error, reportError(error));
      }
    },
    getRow: async (token, customer, rowId) => {
      try {
        return await axios.get(`${url}/customers/${customer}/rows/${rowId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            ContentType: 'application/json'
          }
        });
      } catch (error) {
        throw handleError(error, reportError(error));
      }
    },
    saveRow: async (token, customer, rowData) => {
      const requestBeeContent = JSON.stringify(rowData);
      try {
        return await axios.post(
          `${url}/customers/${customer}/rows`,
          {beeContent: requestBeeContent},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              ContentType: 'application/json'
            }
          }
        );
      } catch (error) {
        throw handleError(error, reportError(error));
      }
    },
    updateRow: async (token, customer, rowData) => {
      const rowId = rowData.metadata.id;
      const requestBeeContent = JSON.stringify(rowData);
      try {
        return await axios.put(
          `${url}/customers/${customer}/rows/${rowId}`,
          {beeContent: requestBeeContent},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              ContentType: 'application/json'
            }
          }
        );
      } catch (error) {
        throw handleError(error, reportError(error));
      }
    },
    deleteRow: async (token, customer, rowId) => {
      try {
        const response = await axios.delete(
          `${url}/customers/${customer}/rows/${rowId}`,
          conf(token)
        );
        return response.data;
      } catch (error) {
        throw handleError(error, reportError(error));
      }
    }
  };
}
